/**
 * arrumar-tipos.tsx — "estes eventos estão no tipo errado; arrume aqui".
 *
 * ─── POR QUE ESTA TELA EXISTE EM VEZ DE UMA REUNIÃO ────────────────────────
 *
 * A leitura da produção em 16/09 mostrou o tipo vazando: médico gravado como
 * `lembrete` e exame como `ausencia` (o mesmo caso em dois tipos), "Feriado 7
 * de Setembro" como `ausencia`, o ajudante Felipe como quatro `lembrete`s,
 * "Medição Fundão" como `lembrete`, "ENTREGAR E INSTALAR" como `retirada`.
 *
 * O plano previa levar a lista para a Suany e ouvir o que cada um é — uma
 * reunião, agendada, que atravessa a semana. Mas **quem sabe o que cada evento
 * é foi quem o lançou**, e ela já está na agenda todo dia. A retipagem vira uma
 * tela que ela resolve sozinha, em dois minutos, quando passar por aqui.
 *
 * ─── COMO ELA DECIDE O QUE MOSTRAR ─────────────────────────────────────────
 *
 * Não adivinha o tipo certo — isso seria trocar um erro por outro com ar de
 * autoridade. Ela mostra os avulsos com o tipo atual e deixa trocar. O que ela
 * faz é ORDENAR: os suspeitos primeiro, com o motivo da suspeita escrito.
 *
 * O suspeito é o `lembrete` que ocupa gente ou lugar — lembrete é o tipo que
 * NÃO ocupa o dia, então um lembrete com equipe ou endereço quase sempre é
 * outra coisa. É heurística de ordenação, nunca de gravação.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';
import type { TipoEvento, VisitaAgenda } from '@/pages/ordens-servico/shared';

const TIPOS: { valor: Exclude<TipoEvento, 'os'>; nome: string; ajuda: string }[] = [
  { valor: 'entrega', nome: 'Entrega', ajuda: 'Levar material ao cliente' },
  { valor: 'retirada', nome: 'Retirada', ajuda: 'Buscar no fornecedor ou na transportadora' },
  { valor: 'medicao', nome: 'Medição', ajuda: 'Ir medir — ocupa a manhã de alguém' },
  { valor: 'servico', nome: 'Serviço na loja', ajuda: 'Faxina, dedetização, manutenção da loja' },
  { valor: 'reuniao', nome: 'Reunião', ajuda: 'Encontro marcado, dentro ou fora' },
  { valor: 'ausencia', nome: 'Ausência', ajuda: 'A pessoa não está — folga, médico, férias' },
  { valor: 'dia_sem_os', nome: 'Dia sem OS', ajuda: 'Afirmação sobre o DIA, não compromisso' },
  { valor: 'lembrete', nome: 'Lembrete', ajuda: 'Um bilhete no dia — não ocupa ninguém' },
];

/** Por que este evento está na fila dos suspeitos. Vazio = não está. */
function suspeita(v: VisitaAgenda): string | null {
  if (v.tipo !== 'lembrete') return null;
  if (v.instaladores?.length) return 'é lembrete e tem gente escalada';
  if (v.endereco || v.bairro || v.cidade) return 'é lembrete e tem endereço';
  return null;
}

export function ArrumarTiposPage() {
  const [eventos, setEventos] = useState<VisitaAgenda[]>([]);
  const [salvando, setSalvando] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const hoje = new Date();
      const iso = (d: Date) => d.toISOString().slice(0, 10);
      const de = iso(new Date(hoje.getFullYear(), hoje.getMonth() - 6, 1));
      const ate = iso(new Date(hoje.getFullYear(), hoje.getMonth() + 6, 0));
      const todos = await api.get<VisitaAgenda[]>(`/agenda/eventos?de=${de}&ate=${ate}`);
      // Só o AVULSO: a ida de OS tira o tipo da OS e não se retipa aqui.
      setEventos(todos.filter((e) => !e.os_id));
    } catch {
      toast.error('Não consegui carregar os eventos.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const ordenados = useMemo(() => {
    const comSuspeita = eventos.filter((e) => suspeita(e));
    const resto = eventos.filter((e) => !suspeita(e));
    return [...comSuspeita, ...resto];
  }, [eventos]);

  async function trocar(v: VisitaAgenda, tipo: string) {
    if (!v.id || tipo === v.tipo) return;
    setSalvando(v.id);
    try {
      // PATCH, não PUT: aqui se muda UMA coisa, e o PUT zeraria status e
      // equipe de quem só queria arrumar a etiqueta.
      await api.patch(`/agenda/eventos/${v.id}`, { tipo });
      setEventos((atual) =>
        atual.map((e) => (e.id === v.id ? { ...e, tipo: tipo as TipoEvento } : e)),
      );
      toast.success('Tipo corrigido.');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não consegui trocar o tipo.');
    } finally {
      setSalvando(null);
    }
  }

  const suspeitos = ordenados.filter((e) => suspeita(e)).length;

  return (
    <CustomMolduraPagina
      titulo="Arrumar os tipos"
      sobrescrito="Agenda"
      descricao="O tipo decide a cor, se o dia fica ocupado e se o evento vai para o roteiro. Trocar aqui não move nada de lugar."
      caminho={[{ rotulo: 'Agenda', para: '/agenda/agendamentos' }]}
    >
      {carregando && <p className="text-secondary">Carregando…</p>}

      {!carregando && suspeitos > 0 && (
        <div className="alert alert-info py-2 small">
          <i className="bi bi-lightbulb me-2"></i>
          {suspeitos} {suspeitos === 1 ? 'evento parece' : 'eventos parecem'} estar no
          tipo errado — {suspeitos === 1 ? 'está' : 'estão'} no topo da lista, com o
          motivo. O palpite é só a ordem: quem sabe o que cada um é é quem lançou.
        </div>
      )}

      {!carregando && eventos.length === 0 && (
        <p className="text-secondary">Nenhum evento avulso nos últimos seis meses.</p>
      )}

      {!carregando && eventos.length > 0 && (
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th>Quando</th>
              <th>O que está escrito</th>
              <th style={{ width: '13rem' }}>É o quê</th>
            </tr>
          </thead>
          <tbody>
            {ordenados.map((v) => {
              const motivo = suspeita(v);
              return (
                <tr key={v.id}>
                  <td className="text-nowrap small text-secondary">
                    {formatarData(v.data)}
                  </td>
                  <td>
                    {v.titulo || <span className="text-secondary">sem título</span>}
                    {motivo && (
                      <span className="d-block small text-warning-emphasis">
                        <i className="bi bi-exclamation-circle me-1"></i>
                        {motivo}
                      </span>
                    )}
                    {v.instaladores?.length > 0 && (
                      <span className="d-block small text-secondary">
                        {v.instaladores.map((i) => i.nome).join(', ')}
                      </span>
                    )}
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={v.tipo}
                      disabled={salvando === v.id}
                      onChange={(e) => trocar(v, e.target.value)}
                    >
                      {TIPOS.map((t) => (
                        <option key={t.valor} value={t.valor} title={t.ajuda}>
                          {t.nome}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </CustomMolduraPagina>
  );
}

export default ArrumarTiposPage;
